import cn from 'classnames';
import { forwardRef, useMemo } from 'react';

import TableSectionContext from '../table/table-section-context';

import type { ForwardedRef, ReactElement } from 'react';

import type { TableSectionContextProps } from '../table/table.types';
import type { TableFootProps } from './table-foot.types';

import './table-foot.scss';

/**
 * The component renders a `<tfoot>` element and should contain `TableRow` components.
 *
 * ### Usage
 *
 * ```tsx
 * import { TableFoot, TableRow, TableCell } from '@bloomreach/react-banana-ui';
 *
 * export default function MyCustomTableFooter() {
 *   return (
 *      <TableFoot className="custom-footer">
 *        <TableRow>
 *          <TableCell>This cell will be rendered as `<td>`</TableCell>
 *        </TableRow>
 *     </TableFoot>
 *   );
 * }
 * ```
 */
const TableFoot = forwardRef(
  (props: TableFootProps, forwardedRef: ForwardedRef<HTMLTableSectionElement> | null): ReactElement => {
    const {
      children,
      className,
      hasPagination,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const contextValue = useMemo<TableSectionContextProps>(() => ({ hasPagination, variant: 'foot' }), [hasPagination]);

    return (
      <TableSectionContext.Provider value={contextValue}>
        <tfoot className={cn('rbui-table-foot', className)} ref={forwardedRef} {...rest}>
          {children}
        </tfoot>
      </TableSectionContext.Provider>
    );
  },
);

TableFoot.displayName = 'TableFoot';

export default TableFoot;
